import { API_URLS } from '@helpers/api';
import { withMotionMax } from '@hoc/withMotionMax';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import NewsCard from '@stories/Components/Cards/NewsCard/NewsCard';
import CarouselProvider from '@stories/Components/Global/Carousel/CarouselProvider/CarouselProvider';
import MotionTrack from '@stories/Components/Global/Carousel/MotionTrack/MotionTrack';
import SectionTitle from '@stories/Components/Global/SectionTitle/SectionTitle';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import Pagination from '@stories/Components/Pagination/Pagination';
import { AnimatePresence, AnimateSharedLayout, MotionConfig, m } from 'framer-motion';
import queryString from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FocusRing } from 'react-aria';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import { NewsListingStyles as S } from './NewsListing.styles';
const NewsListing = ({ sectionTitle, initialCards, filters, translations, ...props }) => {
    // Create a ref for the container
    const containerRef = useRef(null);
    const [activeItems, setActiveItems] = useState([]);
    const [updates, setUpdates] = useState(0);
    const [params, setParams] = useQueryParams({
        docId: withDefault(NumberParam, props.pageContext?.docId),
        page: withDefault(NumberParam, 1),
        filter: withDefault(ArrayParam, filters.map((x) => x.value)),
        sort: withDefault(StringParam, 'default'),
    });
    const initialRender = useMemo(() => updates < 1, [updates]);
    const { response, status } = useApi({
        validate: () => {
            // No clue how else to do this. Setting initialRender/activeItems/params causes re-renders, so can't use them reliably in something like validate() //
            // If we set some kind of flag like 'initialRender', setting it to false causes a re-render, so fetches anyway //
            // Need some way to pass a dependency-array type prop into validate so that it only listens to a select number of state updates //
            return updates > 1;
        },
        url: `${API_URLS.NEWS_LISTING}?${queryString.stringify(params)}`,
        initialData: initialCards,
    });
    useEffect(() => {
        if (containerRef.current && status !== 'loading') {
            if (sectionTitle?.title == '') {
                const offset = containerRef.current.offsetTop;
                const navHeight = 120;
                window.scrollTo({
                    top: offset - navHeight,
                    behavior: 'smooth',
                });
            }
            else {
                containerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }
    }, [response, params, activeItems, status, sectionTitle]);
    useEffect(() => {
        setUpdates(updates + 1);
    }, [activeItems, params]);
    useEffect(() => {
        if (!initialRender) {
            setParams({ page: 1 });
        }
    }, [params.filter]);
    useEffect(() => {
        if (!initialRender) {
            setParams({ page: 1 });
        }
    }, [params.sort]);
    useEffect(() => {
        if (initialRender) {
            if (params.filter) {
                const matchingFilter = filters.find((x) => params.filter.includes(x.value));
                if (matchingFilter) {
                    setActiveItems([matchingFilter]);
                }
                else {
                    setActiveItems([filters[0]]);
                }
            }
            else {
                setActiveItems([filters[0]]);
            }
        }
    }, [params.filter, setParams]);
    useEffect(() => {
        if (!initialRender) {
            setParams({ filter: activeItems.map((x) => x.value) });
        }
    }, [activeItems, setParams]);
    const toggleItem = (item) => {
        if (activeItems.some((x) => x.value === item.value)) {
            if (item.value !== filters[0].value) {
                if (activeItems.filter((x) => x.value !== item.value).length === 0) {
                    setActiveItems([filters[0]]);
                }
                else {
                    setActiveItems(activeItems.filter((x) => x.value !== item.value && x.value !== filters[0].value));
                }
            }
        }
        else {
            if (item.value === filters[0].value) {
                setActiveItems([item]);
            }
            else {
                setActiveItems([...activeItems.filter((x) => x.value !== filters[0].value), item]);
            }
        }
    };
    const handlePageChange = (newPage) => {
        setParams({ page: newPage });
    };
    return (React.createElement(S.Container, { ref: containerRef },
        React.createElement(SectionWrapper, null,
            sectionTitle && React.createElement(SectionTitle, { ...sectionTitle }),
            React.createElement(MotionConfig, { transition: { duration: 0.35, ease: 'easeOut', type: 'tween' } },
                React.createElement(CarouselProvider, { count: filters.length },
                    React.createElement(S.FilterBar, null,
                        React.createElement(MotionTrack, null, filters.map((filter, i) => (React.createElement(S.Filter, { active: activeItems.some((x) => filter.value === x.value), key: `NewsListingFilter_${i}` },
                            React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                                React.createElement(S.FilterInner, { type: "button", onClick: () => toggleItem(filter) }, filter.text))))))))),
            response && (React.createElement(S.SortBar, null,
                React.createElement(S.Count, null,
                    React.createElement("span", null,
                        translations['hyve.listing.showing'],
                        ":"),
                    " ",
                    response.data.length,
                    ' ',
                    translations['hyve.listing.news']?.toLowerCase()),
                React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                    React.createElement(S.SortBy, { value: params.sort, onChange: (e) => setParams({ sort: e.currentTarget.value }), placeholder: translations['hyve.listing.sort'] },
                        React.createElement("option", { value: "default" }, translations['hyve.listing.sort.placeholder']),
                        React.createElement("option", { value: "start-earliest" }, translations['hyve.listing.sort.start-earliest']),
                        React.createElement("option", { value: "start-furthest" }, translations['hyve.listing.sort.start-furthest']))))),
            response && (React.createElement(S.CardsGrid, { loading: status === 'loading' },
                React.createElement(AnimateSharedLayout, null,
                    React.createElement(AnimatePresence, null, response.data.map((card, i) => (React.createElement(m.div, { layoutId: `NewsListing_${card.title.replace(/ /g, '_')}`, key: `NewsListing_${card.title.replace(/ /g, '_')}` },
                        React.createElement(NewsCard, { ...card, translations: translations, theme: i % 2 === 0 ? 'buff' : 'pink', onTagClick: () => {
                                toggleItem(card.tag);
                            } })))))))),
            response && (React.createElement(S.PaginationWrapper, null,
                React.createElement(Pagination, { currentPage: params.page, pages: response.pages, onChange: handlePageChange }))))));
};
export default withMotionMax(withQueryParams(NewsListing));
